import type { TFunction } from 'i18next';
import { type ReactNode, useState } from 'react';
import { ChevronRight } from 'react-bootstrap-icons';

import { MFA_FACTOR } from '@repo/common';
import { DataTestIds } from '@repo/frontend-shared';
import { SDSButton } from '@repo/sds/SDSButton';
import { SDSDialog } from '@repo/sds/SDSDialog';
import { SDSFlexbox } from '@repo/sds/SDSFlexbox';
import { SDSIcon } from '@repo/sds/SDSIcon';
import { SDSPrimitiveButton } from '@repo/sds/SDSPrimitiveButton';
import { SDSText } from '@repo/sds/SDSText';

import smsSetupIntro from './assets/sms-setup-intro.svg';
import {
  ManageActionRow,
  MfaManageDetailRow,
  MfaManageLayout,
  MfaPrimaryChoiceList,
  MfaRemoveConfirmDialog,
  type NewPrimaryMethod,
} from './mfa-authenticator-manage';
import { figmaReplacementChoices, formatMfaFactorDate } from './mfa-authenticator-manage-utils';
import { MfaHero } from './mfa-hero';
import { AuthenticatorAppIcon, EmailCodeMethodIcon, PasskeyMethodIcon } from './mfa-method-icons';
import { resolvePrimaryFactor } from './mfa-method-picker-utils';
import { MfaSettingsPage } from './mfa-settings-page';

const MFA = DataTestIds.COMMON.SECURITY.MFA;

function AddMethodRow({
  description,
  icon,
  onClick,
  testId,
  title,
}: {
  description: string;
  icon: ReactNode;
  onClick: () => void;
  testId: string;
  title: string;
}) {
  return (
    <div className="MfaMethodChoice__option">
      <SDSPrimitiveButton ariaLabel={title} className="MfaMethodChoice__hit" dataTestId={testId} onClick={onClick}>
        <span aria-hidden="true" className="MfaMethodChoice__icon MfaMethodChoice__icon--badge">
          {icon}
        </span>
        <span className="MfaMethodChoice__copy">
          <SDSText className="MfaMethodChoice__title" text={title} type="body-medium" />
          <SDSText color="muted" text={description} type="body-small" />
        </span>
        <span aria-hidden="true" className="MfaMethodChoice__chevron">
          <SDSIcon color="text-secondary" icon={<ChevronRight />} size="small" />
        </span>
      </SDSPrimitiveButton>
    </div>
  );
}

function AddMethodList({
  onChooseEmail,
  onChoosePasskey,
  onChooseTotp,
  t,
}: {
  onChooseEmail: () => void;
  onChoosePasskey: () => void;
  onChooseTotp: () => void;
  t: TFunction<'common'>;
}) {
  return (
    <SDSFlexbox className="MfaMethodChoice MfaMethodChoice--desktop" direction="column" fullWidth gap="text-medium">
      <AddMethodRow
        description={t('security.mfa.totpMethodDescription')}
        icon={<AuthenticatorAppIcon />}
        onClick={onChooseTotp}
        testId={MFA.CHOOSE_TOTP}
        title={t('security.mfa.totpTitle')}
      />
      <AddMethodRow
        description={t('security.mfa.emailMethodDescription')}
        icon={<EmailCodeMethodIcon />}
        onClick={onChooseEmail}
        testId={MFA.CHOOSE_EMAIL}
        title={t('security.mfa.emailMethodTitle')}
      />
      <AddMethodRow
        description={t('security.mfa.passkeyMethodDescription')}
        icon={<PasskeyMethodIcon />}
        onClick={onChoosePasskey}
        testId={MFA.CHOOSE_PASSKEY}
        title={t('security.mfa.passkeyMethodTitle')}
      />
    </SDSFlexbox>
  );
}

export function MfaSmsManageDialog({
  accountPrimary,
  enrolled,
  enrolledAt,
  isMobile,
  lastUsedAt,
  onChangePhone,
  onClose,
  onRemove,
  smsPhoneMasked,
  t,
}: {
  accountPrimary?: string;
  enrolled: readonly string[];
  enrolledAt?: string | null;
  isMobile: boolean;
  lastUsedAt?: string | null;
  onChangePhone: () => void;
  onClose: () => void;
  onRemove: () => void;
  smsPhoneMasked?: string | null;
  t: TFunction<'common'>;
}) {
  const addedOn = formatMfaFactorDate(enrolledAt);
  const lastUsedOn = formatMfaFactorDate(lastUsedAt);
  const isPrimary = resolvePrimaryFactor(enrolled, accountPrimary) === MFA_FACTOR.Sms;
  const title = t('security.mfa.manageSmsTitle');
  const details = (
    <SDSFlexbox
      className={isMobile ? 'MfaAuthenticatorManage MfaAuthenticatorManage--mobile' : 'MfaAuthenticatorManage'}
      direction="column"
      fullWidth
    >
      {smsPhoneMasked ? <MfaManageDetailRow label={t('security.mfa.smsPhoneLabel')} value={smsPhoneMasked} /> : null}
      <MfaManageDetailRow
        badge={
          isPrimary ? (
            <span className="MfaSettingsSection__pill MfaSettingsSection__pill--on">
              <SDSText color="primary" text={t('security.mfa.rolePrimary')} type="body-small" />
            </span>
          ) : undefined
        }
        label={t('security.mfa.methodStatus')}
        value={t(isPrimary ? 'security.mfa.yes' : 'security.mfa.no')}
      />
      {addedOn ? (
        <MfaManageDetailRow dataTestId={MFA.SMS_ADDED} label={t('security.mfa.added')} value={addedOn} />
      ) : null}
      {lastUsedOn ? (
        <MfaManageDetailRow dataTestId={MFA.SMS_LAST_USED} label={t('security.mfa.lastUsed')} value={lastUsedOn} />
      ) : null}
    </SDSFlexbox>
  );
  const actions = (
    <div className="MfaAuthenticatorManage__actions">
      <ManageActionRow
        description={t('security.mfa.changePhoneDescription')}
        onClick={onChangePhone}
        testId={MFA.SMS_CHANGE_NUMBER}
        title={t('security.mfa.changePhone')}
      />
      <ManageActionRow
        danger
        description={isPrimary ? t('security.mfa.removeTotpChoosePrimaryFirst') : undefined}
        onClick={onRemove}
        testId={MFA.SMS_REMOVE}
        title={t('security.mfa.removeSms')}
      />
    </div>
  );

  return (
    <MfaManageLayout
      actions={actions}
      className="MfaTotpDialog MfaAuthenticatorManageDialog MfaSmsManageDialog"
      closeTestId={MFA.SMS_CLOSE}
      dataTestId={MFA.SMS_MANAGE}
      hero={<MfaHero plain={isMobile} src={smsSetupIntro} />}
      isMobile={isMobile}
      onClose={onClose}
      t={t}
      title={title}
    >
      {details}
    </MfaManageLayout>
  );
}

export function MfaSmsRemoveDialog({
  accountPrimary,
  email,
  enrolled,
  isMobile,
  onBack,
  onClose,
  onConfirm,
  smsPhoneMasked,
  t,
}: {
  accountPrimary?: string;
  email?: string;
  enrolled: readonly string[];
  isMobile: boolean;
  onBack: () => void;
  onClose: () => void;
  onConfirm: (replacement?: NewPrimaryMethod) => void;
  smsPhoneMasked?: string | null;
  t: TFunction<'common'>;
}) {
  const totpEnrolled = enrolled.includes(MFA_FACTOR.Totp);
  const emailEnrolled = enrolled.includes(MFA_FACTOR.Email);
  const isPrimary = resolvePrimaryFactor(enrolled, accountPrimary) === MFA_FACTOR.Sms;
  const needsPrimary = isPrimary;
  const [selected, setSelected] = useState<NewPrimaryMethod | undefined>();
  const canRemove = needsPrimary ? Boolean(selected) : true;
  const confirmRemove = () => {
    if (needsPrimary) {
      if (!selected) {
        return;
      }
      onConfirm(selected);
      return;
    }
    onConfirm();
  };
  const primaryChoices = figmaReplacementChoices('sms', t, {
    email: emailEnrolled ? email : undefined,
    passkeyCount: enrolled.includes(MFA_FACTOR.Passkey) ? 1 : 0,
    smsPhoneMasked: smsPhoneMasked ?? undefined,
    totpEnrolled,
  });
  const primaryField = needsPrimary ? (
    <MfaPrimaryChoiceList
      name="mfa-sms-new-primary"
      onSelect={setSelected}
      options={primaryChoices}
      selected={selected}
    />
  ) : null;

  return (
    <MfaRemoveConfirmDialog
      backLabel={t('security.mfa.backToSecurity')}
      cancelLabel={t('security.mfa.cancel')}
      confirmDisabled={!canRemove}
      confirmLabel={needsPrimary ? t('security.mfa.setPrimaryAndRemove') : t('security.mfa.removeSms')}
      dataTestId={MFA.SMS_REMOVE_CONFIRM}
      dialogClassName="MfaTotpDialog MfaRemoveConfirmDialog"
      hero={<MfaHero plain={isMobile} src={smsSetupIntro} />}
      isMobile={isMobile}
      lead={needsPrimary ? t('security.mfa.removeTotpChoosePrimaryLead') : t('security.mfa.removeSmsDescription')}
      onBack={onBack}
      onClose={onClose}
      onConfirm={confirmRemove}
      title={t('security.mfa.removeSmsTitle')}
    >
      {primaryField}
    </MfaRemoveConfirmDialog>
  );
}

export function MfaAddAnotherMethodDialog({
  isMobile,
  onBack,
  onCancel,
  onChooseEmail,
  onChoosePasskey,
  onChooseTotp,
  t,
}: {
  isMobile: boolean;
  onBack: () => void;
  onCancel: () => void;
  onChooseEmail: () => void;
  onChoosePasskey: () => void;
  onChooseTotp: () => void;
  t: TFunction<'common'>;
}) {
  const title = t('security.mfa.addAnotherMethodTitle');
  const methods = (
    <AddMethodList onChooseEmail={onChooseEmail} onChoosePasskey={onChoosePasskey} onChooseTotp={onChooseTotp} t={t} />
  );

  if (isMobile) {
    return (
      <MfaSettingsPage
        actions={
          <>
            <SDSButton
              dataTestId={MFA.TOTP_BACK}
              isFullWidth
              mode="outline"
              onClick={onBack}
              text={t('security.mfa.totpBack')}
            />
            <SDSButton dataTestId={MFA.CANCEL} isFullWidth onClick={onCancel} text={t('security.mfa.cancel')} />
          </>
        }
        ariaLabel={title}
        backLabel={t('security.mfa.backToSecurity')}
        dataTestId={MFA.ADD_ANOTHER_METHOD}
        onBack={onCancel}
      >
        <SDSText text={title} type="h5" />
        {methods}
      </MfaSettingsPage>
    );
  }

  return (
    <SDSDialog
      className="MfaSmsManageDialog"
      dataTestId={MFA.ADD_ANOTHER_METHOD}
      isOpen
      onClose={onCancel}
      primaryAction={{ dataTestId: MFA.CANCEL, label: t('security.mfa.cancel'), onClick: onCancel }}
      secondaryAction={{ dataTestId: MFA.TOTP_BACK, label: t('security.mfa.totpBack'), onClick: onBack }}
      showCloseButton
      size="small"
      title={title}
    >
      {methods}
    </SDSDialog>
  );
}
